import * as Dialog from '@radix-ui/react-dialog';

import { styled } from '@lichtblick/styled';
import { BorderRadius, Colors, Spacing } from '@lichtblick/theme';

import { MessageDialog } from '../Dialog';
import { IconButton } from '../IconButton';

export const DeleteDialog = styled(MessageDialog)`
  background-color: ${Colors.red[10]};
  border: 2px solid ${Colors.red[100]};

  .dialogCloseButton {
    background-color: ${Colors.red[100]};
    svg {
      color: ${Colors.white};
    }
  }

  > div {
    > button:first-child {
      background-color: ${Colors.red[100]};
    }

    > button:nth-child(2) {
      border-color: ${Colors.red[10]};
      color: ${Colors.red[100]};
    }
  }
`;

export const BtnWrapper = styled.div`
  align-items: center;
  display: flex;
  gap: ${Spacing.S};
`;

export const GridViewContainer = styled.div`
  overflow-x: hidden;
  width: 100%;
`;

export const Grid = styled.div`
  column-gap: ${Spacing.M};
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(11rem, 1fr));
  margin-top: ${Spacing.M};
  row-gap: ${Spacing.M};
  width: 100%;

  @media (max-width: 600px) {
    column-gap: ${Spacing.S};
    grid-template-columns: 1fr;
  }
`;

export const GridItemContainer = styled.div`
  align-items: center;
  border: 1px solid ${Colors.blue[20]};
  border-radius: ${BorderRadius.S};
  cursor: pointer;
  display: flex;
  height: 11rem;
  justify-content: center;
  overflow: hidden;
  position: relative;
  width: 11rem;

  div:not([data-checkbox='true']) {
    width: 100%;
  }

  @media (max-width: 600px) {
    flex-direction: column;
    height: auto;
    width: 100%;
  }

  > div {
    width: 100%;
  }
`;

export const HoverContainer = styled.div<{ $isShowing: boolean }>`
  align-items: center;
  background-color: ${Colors.darkBlue};
  bottom: ${({ $isShowing }) => ($isShowing ? 0 : '-2rem')};
  display: flex;
  height: 2rem;
  padding: 0 ${Spacing['3Xs']} 0 ${Spacing['Xxs']};
  position: absolute;
  transition: bottom 0.2s;
  z-index: 1;
  > div {
    align-items: center;
    display: flex;
    gap: ${Spacing['3Xs']};
    justify-content: flex-end;
  }
`;

export const CarouselContainerOverlay = styled(Dialog.Overlay)`
  align-items: center;
  background: ${Colors.darkBlue};
  display: flex;
  height: 100vh;
  justify-content: center;
  left: 0;
  overflow-y: auto;
  pointer-events: auto;
  position: fixed;
  top: 0;
  width: 100vw;
  z-index: 2;
`;

export const CarouselDocContainer = styled(Dialog.Content)`
  align-items: center;
  background: ${Colors.white};
  border-radius: 0.5rem;
  display: flex;
  flex-direction: column;
  gap: ${Spacing.Xs};
  height: 90vh;
  justify-content: center;
  left: 0;
  margin: auto;
  max-width: 60.75rem;
  overflow-y: scroll;
  padding: ${Spacing.Xl} ${Spacing.S};
  position: relative;
  top: 5;
  width: 90%;

  @media (max-width: 600px) {
    border-radius: 0;
    height: 100vh;
    max-width: none;
    padding: 3rem 0 3rem 0;
    width: 100%;
  }
`;

export const CarouselVideoContainer = styled.div`
  align-items: center;
  background: ${Colors.white};
  border-radius: 0.5rem;
  display: flex;
  flex-direction: column;
  gap: ${Spacing.Xs};
  height: 90vh;
  justify-content: center;
  margin: auto;
  max-width: 60.75rem;
  overflow-y: scroll;
  padding: ${Spacing.L} ${Spacing.S};
  pointer-events: auto;
  position: relative;
  width: 90%;

  @media (max-width: 600px) {
    border-radius: 0;
    height: 100vh;
    max-width: none;
    padding: 3rem 0 3rem 0;
    width: 100%;
  }
`;

export const PreviewWrapper = styled.div`
  align-items: center;
  display: flex;
  height: 100%;
  justify-content: center;
  width: 100%;
`;

export const IconButtonClose = styled(IconButton)`
  align-items: center;
  background: rgba(0, 0, 0, 0.7);
  border: none;
  border-radius: 50%;
  color: white;
  cursor: pointer;
  display: flex;
  height: 40px;
  justify-content: center;
  pointer-events: auto;
  position: fixed;
  right: 24px;
  top: 5px;
  transition: background 0.2s ease;
  width: 40px;
  z-index: 10000;

  &:hover {
    background: rgba(0, 0, 0, 0.9);
  }

  &:focus {
    outline: 2px solid ${Colors.blue[100]};
    outline-offset: 2px;
  }
`;

export const DeleteContainer = styled.div`
  align-items: center;
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
  margin-left: auto;
`;

export const CheckboxDeleteContainer = styled.div`
  align-items: center;
  display: flex;
  gap: 8px;
`;

export const SingleCheckboxWrapper = styled.div`
  display: flex;
  height: 8px;
  margin-right: 0px;
  transform: scale(0.75);
  width: 8px;
  z-index: 1;
`;

export const MobileImageWrapper = styled.div`
  align-items: center;
  display: flex;
  justify-content: center;
  width: 100%;

  @media (max-width: 600px) {
    height: 150px;
    width: 100%;

    * {
      max-height: 130px !important;
      max-width: 100% !important;
      object-fit: contain;
    }

    img {
      height: auto;
      width: auto;
    }
  }
`;

export const MobileControlsContainer = styled.div`
  align-items: center;
  background-color: ${Colors.darkBlue};
  display: flex;
  gap: ${Spacing.S};
  width: 100%;

  @media (max-width: 600px) {
    background-color: ${Colors.blue[5]};
    border-top: 1px solid ${Colors.blue[20]};
    flex-wrap: wrap;
    gap: ${Spacing.S};
    justify-content: flex-start;
    padding: ${Spacing.S};

    svg {
      height: 1.8rem;
      width: 1.8rem;
    }
  }
`;

export const MobileFileName = styled.div`
  color: ${Colors.white};
  font-size: 0.75rem;
  font-weight: 500;
  text-align: left;

  @media (max-width: 600px) {
    color: ${Colors.gray[100]};
    flex: 0 1 auto;
    font-size: 0.875rem;
    margin-right: ${Spacing.S};
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

export const MobileButtonsGroup = styled.div<{ $alignRight?: boolean }>`
  align-items: center;
  display: flex;
  gap: ${Spacing.M};
  justify-content: ${({ $alignRight }) => ($alignRight ? 'flex-end' : 'flex-start')};
  width: ${({ $alignRight }) => ($alignRight ? 'auto' : '100%')};
`;

/** Only visible on viewports ≤600px — hidden on desktop via CSS. */
export const GridMobileOnly = styled.div`
  display: none;

  @media (max-width: 600px) {
    display: contents;
  }
`;

/** Only visible on viewports >600px — hidden on mobile via CSS. */
export const GridDesktopOnly = styled.div`
  display: contents;

  @media (max-width: 600px) {
    display: none;
  }
`;
